/**
 * User Mapper Utility
 * Maps between API response format and form/table display format
 */

import type { User, UserTableRow } from '../types/user.types';
import { RIS_API_BASE_URL } from '../config/api.config';
import { formatDateTimeShort } from './dateFormatter';

/**
 * Format 24h time "HH:mm" or "HH:mm:ss" to "10:00 AM" for API availability_hours
 */
function formatTime24To12(time: string): string {
  if (!time || !time.trim()) {
    return '';
  }
  const parts = time.trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1] ? parseInt(parts[1], 10) : 0;
  if (Number.isNaN(h)) {
    return '';
  }
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

/**
 * Format time for display (e.g. "09:00" or ISO datetime -> "9:00 AM"). Accepts 24h, 12h, or ISO.
 */
export function formatTimeForDisplay(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '';
  }
  const s = value.trim();
  if (/^\d{1,2}:\d{2}(?::\d{2})?\s*(AM|PM)$/i.test(s)) {
    return s;
  }
  // ISO / datetime: use wall time after T (ignore date; avoids broken parse from "2024-01-01T09:30:00")
  const isoTime = s.match(/T(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i);
  if (isoTime) {
    const h = parseInt(isoTime[1], 10);
    const m = isoTime[2].padStart(2, '0');
    if (!Number.isNaN(h) && h >= 0 && h <= 23) {
      return formatTime24To12(`${h.toString().padStart(2, '0')}:${m}`);
    }
  }
  const normalized = normalizeTimeForInput(s);
  if (!normalized) {
    return s;
  }
  return formatTime24To12(normalized);
}

/**
 * Normalize time to "HH:mm" (24h) for time input. Accepts "HH:mm", "HH:mm:ss", or "h:mm AM/PM".
 */
function normalizeTimeForInput(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '';
  }
  const s = value.trim();
  const iso = s.match(/T(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?/i);
  if (iso) {
    const h = parseInt(iso[1], 10);
    const m = iso[2].padStart(2, '0');
    if (!Number.isNaN(h) && h >= 0 && h <= 23) {
      return `${h.toString().padStart(2, '0')}:${m}`;
    }
  }
  const amPmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (amPmMatch) {
    let h = parseInt(amPmMatch[1], 10);
    const m = amPmMatch[2];
    const period = amPmMatch[3].toUpperCase();
    if (period === 'PM' && h !== 12) {
      h += 12;
    }
    if (period === 'AM' && h === 12) {
      h = 0;
    }
    return `${h.toString().padStart(2, '0')}:${m}`;
  }
  const parts = s.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1] ? parts[1].replaceAll(/\D/g, '').slice(0, 2).padStart(2, '0') : '00';
  if (Number.isNaN(h) || h < 0 || h > 23) {
    return '';
  }
  return `${h.toString().padStart(2, '0')}:${m}`;
}

function normalizeStringArray(value: string[] | string | null | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map(v => String(v).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }
  return [];
}

/** Postgres `text[]` literal string; use for JSON bodies that reject JS arrays (e.g. user update). */
export function toPostgresArrayLiteral(values: string[]): string {
  const backslash = String.fromCodePoint(92);
  const escapedBackslash = String.raw`\\`;
  const escaped = values.map(value => {
    const withSlashes = value.replaceAll(backslash, escapedBackslash);
    const withQuotes = withSlashes.replaceAll('"', `${backslash}"`);
    return `"${withQuotes}"`;
  });
  return `{${escaped.join(',')}}`;
}

/**
 * Extract filename from file path
 * @param path - File path (e.g., "/uploads/signatures/file.jpeg")
 * @returns Filename (e.g., "file.jpeg") or empty string if path is invalid
 */
export function extractFilenameFromPath(path: string | null | undefined): string {
  if (!path) {
    return '';
  }
  // Extract filename from path (handle both forward and backward slashes)
  const parts = path.split(/[/\\]/);
  const raw = parts.at(-1) || '';
  // Decode URL-encoded characters (e.g. %20 -> space) so filename displays properly
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Build an absolute URL for a signature path returned by the API (e.g. "/uploads/signatures/file.pdf").
 */
export function resolveSignatureAssetUrl(path: string | null | undefined): string | null {
  if (path == null || String(path).trim() === '') {
    return null;
  }
  const trimmedPath = String(path).trim();
  if (/^https?:\/\//i.test(trimmedPath)) {
    return trimmedPath;
  }
  const apiBaseUrl = RIS_API_BASE_URL.replace(/\/$/, '');
  if (trimmedPath.startsWith('/')) {
    return `${apiBaseUrl}${trimmedPath}`;
  }
  return `${apiBaseUrl}/${trimmedPath}`;
}

/**
 * Map API response to table display format
 */
export function mapApiResponseToTableFormat(user: User): UserTableRow {
  // For Radiologist, use doctor_id; for Technician, use employee_id; for ReferringPhysician, no employee_id
  const employeeId =
    user.role === 'Radiologist'
      ? user.doctor_id || ''
      : user.role === 'ReferringPhysician'
        ? '—'
        : user.employee_id || '';

  // For Radiologist, use reporting_modality_access; for Technician, use department
  const department =
    user.role === 'Radiologist'
      ? Array.isArray(user.reporting_modality_access)
        ? user.reporting_modality_access.join(', ')
        : user.reporting_modality_access || ''
      : Array.isArray(user.department)
        ? user.department.join(', ')
        : user.department || '';

  return {
    id: user.id?.toString() || '--',
    slNo: 0, // Will be set by the component
    active: user.status === 'Active',
    employeeId: employeeId || '--',
    fullName: user.full_name || '--',
    role: user.role || '--',
    emailId: user.email || '--',
    username: user.username || '--',
    phoneNo: user.mobile_number || '--',
    department: department || '--',
    createdBy: user.createdBy?.full_name || '--',
    createdAt: formatDateTimeShort(user.createdAt || user.created_at) || '--',
    lastModifiedBy: user.modifiedBy?.full_name || '--',
    lastModified: formatDateTimeShort(user.updatedAt || user.updated_at) || '--',
  };
}

/**
 * Convert File to base64 string (unused but kept for future use)
 */
// async function fileToBase64(file: File): Promise<string> {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.readAsDataURL(file);
//     reader.onload = () => {
//       const result = reader.result as string;
//       // Remove data URL prefix (e.g., "data:image/png;base64,")
//       const base64 = result.split(',')[1];
//       resolve(base64);
//     };
//     reader.onerror = error => reject(error);
//   });
// }

function appendFacilityId(formData: FormData, role: string, facilityId?: string): void {
  if (role === 'Radiologist' || role === 'Technician' || role === 'ReferringPhysician') {
    formData.append('facility_id', facilityId || 'FAC001');
    return;
  }
  formData.append('facility_id', facilityId || 'FAC001');
}

function appendExperienceYears(
  formData: FormData,
  experienceYears: number | string | undefined
): void {
  if (experienceYears === undefined || experienceYears === null || experienceYears === '') {
    return;
  }
  const years =
    typeof experienceYears === 'string'
      ? Number.parseInt(experienceYears, 10)
      : Number(experienceYears);
  if (!Number.isNaN(years) && years >= 0) {
    formData.append('experience_years', years.toString());
  }
}

function appendTechnicianFields(
  formData: FormData,
  values: {
    employeeId: string;
    shiftStart?: string;
    shiftEnd?: string;
  }
): void {
  formData.append('employee_id', values.employeeId);
  if (values.shiftStart) {
    formData.append('shift_start_at', values.shiftStart);
  }
  if (values.shiftEnd) {
    formData.append('shift_end_at', values.shiftEnd);
  }
}

function appendRadiologistFields(
  formData: FormData,
  values: {
    employeeId: string;
    shiftStart?: string;
    shiftEnd?: string;
    availabilityDays?: string[];
    sub_role?: string;
    reportingModalityAccess?: string[] | string;
    registrationNo?: string;
    specialty?: string[] | string;
    designation?: string;
    signature?: File | null;
  }
): void {
  formData.append('doctor_id', values.employeeId);

  if (values.shiftStart) {
    formData.append('shift_start_at', values.shiftStart);
  }
  if (values.shiftEnd) {
    formData.append('shift_end_at', values.shiftEnd);
  }

  if (values.shiftStart && values.shiftEnd) {
    const availabilityHoursStr = `${formatTime24To12(values.shiftStart)} - ${formatTime24To12(values.shiftEnd)}`;
    formData.append('availability_hours', availabilityHoursStr);
  }
  const daysToSend =
    Array.isArray(values.availabilityDays) && values.availabilityDays.length > 0
      ? values.availabilityDays
      : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  daysToSend.forEach(day => {
    formData.append('availability_days', day);
  });

  const subRole = values.sub_role || null;
  if (subRole) {
    formData.append('sub_role', subRole);
  }

  if (values.reportingModalityAccess) {
    const reportingModalityArray = Array.isArray(values.reportingModalityAccess)
      ? values.reportingModalityAccess
      : [values.reportingModalityAccess];
    reportingModalityArray.forEach(modality => {
      formData.append('reporting_modality_access', modality);
    });
  }

  if (values.registrationNo) {
    formData.append('registration_number', values.registrationNo);
  }
  const specialtyValues = normalizeStringArray(values.specialty);
  if (specialtyValues.length) {
    formData.append('specialty', toPostgresArrayLiteral(specialtyValues));
  }
  if (values.designation) {
    formData.append('designation', values.designation);
  }

  if (values.signature && values.signature instanceof File) {
    formData.append('signature', values.signature);
  }
}

function appendReferringPhysicianFields(
  formData: FormData,
  values: {
    specialization?: string[] | string;
    clinicHospitalName?: string;
    reportAccess?: boolean;
    registrationNo?: string;
    qualification?: string;
  }
): void {
  const specializationValues = normalizeStringArray(values.specialization);
  if (specializationValues.length) {
    formData.append('specialization', toPostgresArrayLiteral(specializationValues));
  }
  if (values.clinicHospitalName) {
    formData.append('clinic_hospital_name', values.clinicHospitalName);
  }
  const reportAccessValue =
    values.reportAccess === undefined ? 'true' : String(values.reportAccess);
  formData.append('report_access', reportAccessValue);
  if (values.registrationNo) {
    formData.append('registration_number', values.registrationNo);
  }
  if (values.qualification) {
    formData.append('qualification', values.qualification);
  }
}

/**
 * Map form values to FormData for API create/update request
 */
export async function mapFormValuesToCreateRequest(
  values: {
    fullName: string;
    gender: string;
    dateOfBirth: string;
    username: string;
    email: string;
    phoneNo: string;
    password: string;
    role: string;
    sub_role?: string;
    status: string;
    employeeId: string;
    qualification?: string;
    experienceYears?: number | string;
    // Role-specific fields (may not be in API yet, but kept for form)
    modality?: string[] | string;
    shiftStart?: string;
    shiftEnd?: string;
    registrationNo?: string;
    experience?: string;
    specialty?: string[] | string;
    designation?: string;
    reportingModalityAccess?: string[] | string;
    signature?: File | null;
    availabilityHours?: string[] | string;
    availabilityDays?: string[];
    specialization?: string[] | string;
    clinicHospitalName?: string;
    reportAccess?: boolean;
  },
  facilityId?: string,
  isEdit?: boolean,
  currentUserRole?: string
): Promise<FormData> {
  const formData = new FormData();

  formData.append('full_name', values.fullName);
  formData.append('gender', values.gender);
  formData.append('date_of_birth', values.dateOfBirth);
  formData.append('username', values.username);
  formData.append('email', values.email);
  formData.append('mobile_number', values.phoneNo.replaceAll(/\s+/g, ''));
  formData.append('role', values.role);

  appendFacilityId(formData, values.role, facilityId);

  formData.append('status', values.status === 'active' ? 'Active' : 'Inactive');
  formData.append('tenantId', '700');

  const passwordValue = values.password?.trim();
  const PASSWORD_PLACEHOLDER = '••••••';
  if (passwordValue && passwordValue.length > 0 && passwordValue !== PASSWORD_PLACEHOLDER) {
    formData.append('password', passwordValue);
  }

  appendExperienceYears(formData, values.experienceYears);

  if (values.role === 'Radiologist' && values.qualification) {
    formData.append('qualification', values.qualification);
  }

  if (values.role === 'Technician') {
    appendTechnicianFields(formData, values);
  } else if (values.role === 'Radiologist') {
    appendRadiologistFields(formData, values);
  } else if (values.role === 'ReferringPhysician') {
    appendReferringPhysicianFields(formData, values);
  }

  return formData;
}

function resolveReportingModalityArray(user: User): string[] {
  if (user.role !== 'Radiologist') {
    return [];
  }
  if (Array.isArray(user.reporting_modality_access)) {
    return user.reporting_modality_access;
  }
  if (user.reporting_modality_access) {
    return [user.reporting_modality_access];
  }
  return [];
}

function resolveShiftTimesFromUser(user: User): { shiftStart: string; shiftEnd: string } {
  const shiftStart = '';
  const shiftEnd = '';
  if (user.role !== 'Technician' && user.role !== 'Radiologist') {
    return { shiftStart, shiftEnd };
  }

  if (user.shift_start_at && user.shift_end_at) {
    return {
      shiftStart: normalizeTimeForInput(user.shift_start_at),
      shiftEnd: normalizeTimeForInput(user.shift_end_at),
    };
  }

  if (user.shift_timing) {
    const shiftParts = user.shift_timing.split(' - ');
    if (shiftParts.length === 2) {
      return {
        shiftStart: normalizeTimeForInput(shiftParts[0].trim()),
        shiftEnd: normalizeTimeForInput(shiftParts[1].trim()),
      };
    }
    return { shiftStart, shiftEnd };
  }

  const availHours = Array.isArray(user.availability_hours)
    ? user.availability_hours[0]
    : user.availability_hours;
  if (typeof availHours === 'string' && availHours.includes(' - ')) {
    const [start, end] = availHours.split(' - ').map(s => s.trim());
    if (start && end) {
      return {
        shiftStart: normalizeTimeForInput(start),
        shiftEnd: normalizeTimeForInput(end),
      };
    }
  }

  return { shiftStart, shiftEnd };
}

function mapAvailabilityDays(user: User): string[] {
  const raw = Array.isArray(user.availability_days)
    ? user.availability_days
    : user.availability_days
      ? [user.availability_days]
      : [];
  const dayMap: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
  };
  return raw.map(d => dayMap[d.toLowerCase?.()] ?? d);
}

function mapUserAvailabilityHours(user: User): string[] {
  if (Array.isArray(user.availability_hours)) {
    return user.availability_hours;
  }
  if (user.availability_hours) {
    return [user.availability_hours];
  }
  return [];
}

function mapReferringPhysicianReportAccess(user: User): string | undefined {
  if (user.role !== 'ReferringPhysician') {
    return undefined;
  }
  if (user.report_access === undefined) {
    return 'true';
  }
  return String(user.report_access);
}

/**
 * Map API response to form values
 */
export function mapApiResponseToFormValues(user: User): {
  fullName: string;
  gender: string;
  dateOfBirth: string;
  username: string;
  email: string;
  phoneNo: string;
  password: string;
  role: string;
  sub_role?: string;
  status: string;
  employeeId: string;
  department: string[];
  qualification?: string;
  experienceYears?: number | string;
  reportingModalityAccess?: string[];
  shiftStart?: string;
  shiftEnd?: string;
  registrationNumber?: string;
  specialty?: string;
  designation?: string;
  signatureFileName?: string;
  availabilityHours?: string[];
  availabilityDays?: string[];
  specialization?: string;
  clinicHospitalName?: string;
  reportAccess?: string;
} {
  const departmentArray = Array.isArray(user.department)
    ? user.department
    : user.department
      ? [user.department]
      : [];

  const employeeId = user.role === 'Radiologist' ? user.doctor_id || '' : user.employee_id || '';
  const reportingModalityArray = resolveReportingModalityArray(user);
  const { shiftStart, shiftEnd } = resolveShiftTimesFromUser(user);

  return {
    fullName: user.full_name || '',
    gender: user.gender || '',
    dateOfBirth: user.date_of_birth || '',
    username: user.username || '',
    email: user.email || '',
    phoneNo: user.mobile_number || '',
    password: '',
    role: user.role || '',
    sub_role: user.sub_role || '',
    status: user.status === 'Active' ? 'active' : 'inactive',
    employeeId: employeeId,
    department: departmentArray,
    qualification: user.qualification || '',
    experienceYears:
      user.experience_years !== undefined && user.experience_years !== null
        ? Number(user.experience_years)
        : undefined,
    reportingModalityAccess: reportingModalityArray,
    shiftStart: shiftStart,
    shiftEnd: shiftEnd,
    registrationNumber: user.registration_number || '',
    specialty: normalizeStringArray(user.specialty).join(', '),
    designation: user.designation || '',
    signatureFileName: extractFilenameFromPath(user.signature),
    availabilityHours: mapUserAvailabilityHours(user),
    availabilityDays: mapAvailabilityDays(user),
    specialization:
      user.role === 'ReferringPhysician' ? normalizeStringArray(user.specialty).join(', ') : '',
    clinicHospitalName: user.role === 'ReferringPhysician' ? user.clinic_hospital_name || '' : '',
    reportAccess: mapReferringPhysicianReportAccess(user),
  };
}

function appendTechnicianUpdateFields(request: Record<string, unknown>, user: User): void {
  if (user.employee_id) {
    request.employee_id = user.employee_id;
  }
  if (user.department) {
    request.department = Array.isArray(user.department) ? user.department : [user.department];
  }
  if (user.shift_start_at) {
    request.shift_start_at = user.shift_start_at;
  }
  if (user.shift_end_at) {
    request.shift_end_at = user.shift_end_at;
  }
}

function appendRadiologistUpdateFields(request: Record<string, unknown>, user: User): void {
  if (user.doctor_id) {
    request.doctor_id = user.doctor_id;
  }
  if (user.reporting_modality_access) {
    request.reporting_modality_access = Array.isArray(user.reporting_modality_access)
      ? user.reporting_modality_access
      : [user.reporting_modality_access];
  }
  if (user.registration_number) {
    request.registration_number = user.registration_number;
  }
  if (user.specialty) {
    const specialtyValues = normalizeStringArray(user.specialty);
    if (specialtyValues.length) {
      request.specialty = toPostgresArrayLiteral(specialtyValues);
    }
  }
  if (user.shift_start_at) {
    request.shift_start_at = user.shift_start_at;
  }
  if (user.shift_end_at) {
    request.shift_end_at = user.shift_end_at;
  }
}

function appendReferringPhysicianUpdateFields(request: Record<string, unknown>, user: User): void {
  if (user.employee_id) {
    request.employee_id = user.employee_id;
  }
  if (user.specialty) {
    const specializationValues = normalizeStringArray(user.specialty);
    if (specializationValues.length) {
      request.specialization = toPostgresArrayLiteral(specializationValues);
    }
  }
  if (user.clinic_hospital_name) {
    request.clinic_hospital_name = user.clinic_hospital_name;
  }
  if (user.report_access !== undefined && user.report_access !== null) {
    request.report_access = user.report_access;
  }
  if (user.registration_number) {
    request.registration_number = user.registration_number;
  }
}

/**
 * Map User object to update request format for status toggle
 * This is used when only updating the status field
 */
export function mapUserToUpdateRequest(user: User): Record<string, unknown> {
  const request: Record<string, unknown> = {
    full_name: user.full_name,
    gender: user.gender,
    date_of_birth: user.date_of_birth,
    username: user.username,
    email: user.email,
    mobile_number: user.mobile_number,
    role: user.role,
    facility_id: user.facility_id,
    status: user.status,
    qualification: user.qualification || undefined,
    experience_years: user.experience_years || undefined,
  };

  if (user.password) {
    request.password = user.password;
  }

  if (user.role === 'Technician') {
    appendTechnicianUpdateFields(request, user);
  } else if (user.role === 'Radiologist') {
    appendRadiologistUpdateFields(request, user);
  } else if (user.role === 'ReferringPhysician') {
    appendReferringPhysicianUpdateFields(request, user);
  }

  return request;
}
